/** @format */

import {useEffect} from 'react';

import {ThemeProvider} from '@mui/styles';
import {useCookies} from 'react-cookie';
import {Navigate, Route, Routes} from 'react-router-dom';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource-variable/roboto-flex';
import 'material-symbols/outlined.css';
import './App.css';

import Auth from 'pages/Auth';
import ChiefReport from 'pages/ChiefReport';
import Errors from 'pages/Errors';
import Guest from 'pages/Guest';
import Home from 'pages/Home';
import HomeGrouping from 'pages/HomeGrouping';
import HomeOverview from 'pages/HomeOverview';
import Oops from 'pages/Oops';

import {Materials} from 'pages/Help/Materials';
import ProjectReportsArchive from 'pages/ProjectReports/Archive';
import CompanyReports from 'pages/ProjectReports/CompanyArchive';
import ProjectReportOrder from 'pages/ProjectReports/Order';
import PublicReport from 'pages/ProjectReports/Public';
import ProjectReport from 'pages/ProjectReports/Report';
import CompanyRedirect from 'pages/Redirect';
import Share from 'pages/Share';
import Wizard from 'pages/Wizard';

import PrivateRoute from 'components/PrivateRoute';

import {ROUTES} from 'constants/ROUTES';
import {useAuthContext} from 'contexts/AuthContext';
import {useMixpanelContext} from 'contexts/MixpanelContext';
import {IsRuVersion, secondLevelDomain, useSearchParams} from 'helpers/routerHelpers';
import theme from 'theme';
import {DesignSystemProvider} from 'ui';

import {Tariffs} from './pages/Tariffs';
import {TariffRedirect} from './features/tariffs/TariffRedirect';

// 1 month
const COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

const App = () => {
  const searchParams = useSearchParams();
  const partner = searchParams.get('partner');

  const [, setCookie] = useCookies();

  const {authToken} = useAuthContext();
  const mixpanelContext = useMixpanelContext();
  const domain = secondLevelDomain();

  useEffect(() => {
    if (partner) {
      setCookie('partner', partner, {domain, maxAge: COOKIE_MAX_AGE});
    }

    if (!IsRuVersion()) {
      let config = {ID: '4uL3Rd5AbrGxKW4dn'};
      if (authToken != null) {
        config.clientID = authToken.user.uuid;
      }
    }

    mixpanelContext.setup(authToken);
  }, [partner, setCookie, authToken, mixpanelContext, domain]);

  return (
    <>
      <Routes>
        <Route
          path={`${ROUTES.REDIRECT}`}
          element={
            <PrivateRoute>
              <ThemeProvider theme={theme}>
                <CompanyRedirect />
              </ThemeProvider>
            </PrivateRoute>
          }
        />

        <Route
          path={`${ROUTES.OOPS}`}
          element={
            <ThemeProvider theme={theme}>
              <Oops />
            </ThemeProvider>
          }
        />

        <Route
          path={`${ROUTES.CHIEF_REPORT}/*`}
          element={
            <ThemeProvider theme={theme}>
              <TariffRedirect>
                <ChiefReport />
              </TariffRedirect>
            </ThemeProvider>
          }
        />

        <Route
          path={`${ROUTES.AUTH}/*`}
          element={
            <DesignSystemProvider>
              <Auth />
            </DesignSystemProvider>
          }
        />

        <Route path={`${ROUTES.GUEST}/*`} element={<Guest />} />

        <Route
          path={`${ROUTES.WIZARD}/*`}
          element={
            <PrivateRoute>
              <ThemeProvider theme={theme}>
                <TariffRedirect>
                  <Wizard />
                </TariffRedirect>
              </ThemeProvider>
            </PrivateRoute>
          }
        />

        <Route
          path={`${ROUTES.HOME}/*`}
          element={
            <PrivateRoute>
              <ThemeProvider theme={theme}>
                <TariffRedirect>
                  <Home />
                </TariffRedirect>
              </ThemeProvider>
            </PrivateRoute>
          }
        />

        <Route
          path={`${ROUTES.ERRORS}/*`}
          element={
            <PrivateRoute>
              <ThemeProvider theme={theme}>
                <Errors />
              </ThemeProvider>
            </PrivateRoute>
          }
        />

        <Route
          path={`${ROUTES.SHARE}/*`}
          element={
            <PrivateRoute>
              <ThemeProvider theme={theme}>
                <TariffRedirect>
                  <Share />
                </TariffRedirect>
              </ThemeProvider>
            </PrivateRoute>
          }
        />

        <Route
          path={`${ROUTES.GROUPING}/*`}
          element={
            <PrivateRoute>
              <ThemeProvider theme={theme}>
                <TariffRedirect>
                  <HomeGrouping />
                </TariffRedirect>
              </ThemeProvider>
            </PrivateRoute>
          }
        />

        <Route
          path={`${ROUTES.OVERVIEW}/*`}
          element={
            <PrivateRoute>
              <ThemeProvider theme={theme}>
                <TariffRedirect>
                  <HomeOverview />
                </TariffRedirect>
              </ThemeProvider>
            </PrivateRoute>
          }
        />

        <Route
          path={ROUTES.REPORTS}
          element={
            <PrivateRoute>
              <ThemeProvider theme={theme}>
                <TariffRedirect>
                  <DesignSystemProvider>
                    <ProjectReportsArchive />
                  </DesignSystemProvider>
                </TariffRedirect>
              </ThemeProvider>
            </PrivateRoute>
          }
        />

        <Route
          path={ROUTES.REPORTS_NEW}
          element={
            <PrivateRoute>
              <ThemeProvider theme={theme}>
                <TariffRedirect>
                  <DesignSystemProvider>
                    <ProjectReportOrder />
                  </DesignSystemProvider>
                </TariffRedirect>
              </ThemeProvider>
            </PrivateRoute>
          }
        />

        <Route
          path={ROUTES.REPORT}
          element={
            <PrivateRoute>
              <ThemeProvider theme={theme}>
                <TariffRedirect>
                  <DesignSystemProvider>
                    <ProjectReport />
                  </DesignSystemProvider>
                </TariffRedirect>
              </ThemeProvider>
            </PrivateRoute>
          }
        />

        <Route
          path={ROUTES.COMPANY_REPORTS}
          element={
            <PrivateRoute>
              <ThemeProvider theme={theme}>
                <TariffRedirect>
                  <DesignSystemProvider>
                    <CompanyReports />
                  </DesignSystemProvider>
                </TariffRedirect>
              </ThemeProvider>
            </PrivateRoute>
          }
        />

        <Route
          path={`${ROUTES.TARIFFS}/*`}
          element={
            <PrivateRoute>
              <ThemeProvider theme={theme}>
                <Tariffs />
              </ThemeProvider>
            </PrivateRoute>
          }
        />

        <Route
          path={`${ROUTES.HELP_MATERIALS}`}
          element={
            <ThemeProvider theme={theme}>
              <Materials />
            </ThemeProvider>
          }
        />

        {/* Вне PrivateRoute и вне TariffRedirect. Второе не менее важно
            первого: TariffRedirect спрашивает про currentUser, у гостя
            бэкенд ответит `unauthenticated`, и errorLink перезагрузит
            страницу — раз за разом. */}
        <Route
          path={ROUTES.SHARED_REPORT}
          element={
            <ThemeProvider theme={theme}>
              <DesignSystemProvider>
                <PublicReport />
              </DesignSystemProvider>
            </ThemeProvider>
          }
        />

        <Route path={ROUTES.INDEX} element={<Navigate to={`/${ROUTES.GROUPING}`} />} />
      </Routes>
    </>
  );
};

export default App;
